/* eslint-disable @next/next/no-img-element */
import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTickets } from '@actions/helpCenter';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import Tags from '@base/Tags/Tags';
import dateHelper from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings } from '@styles/index';
import useEducatorContext from '@hooks/useEducatorContext';
import { getImage } from '@utils/imageHelper';

const Wrapper = styled.div`
  grid-column: 4 / 22;
  margin-top: ${Spacings.SPACING_14B};
`;

const SubTitle = styled(H4)`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const Ticket = styled.div`
  padding: ${Spacings.SPACING_6B};
  background-color: var(--color-base-1);
  display: grid;
  grid-template: 1fr / 40px 1fr;
  grid-column-gap: ${Spacings.SPACING_5B};
  align-items: center;
  border-radius: ${Spacings.SPACING_2B};
  margin: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
  box-shadow: ${boxShadowGenerator()};
  cursor: pointer;
`;

const Logo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-i-blue);
`;

const TopContainer = styled.div`
  display: grid;
  grid-template: 1fr / 1fr max-content;
  grid-column-gap: ${Spacings.SPACING_3B};
`;

const Container = styled.div`
  margin-top: ${Spacings.SPACING_1B};
  display: grid;
  grid-template: 1fr / 1fr max-content;
  grid-column-gap: ${Spacings.SPACING_3B};
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusLabel = styled(P2)`
  margin-right: ${Spacings.SPACING_1B};
`;

const Tag = styled(Tags)`
  padding-top: ${Spacings.SPACING_1B};
  padding-bottom: ${Spacings.SPACING_1B};
  color: ${({ $isInProgress }) =>
    $isInProgress ? 'var(--color-text-secondary)' : ''};
`;

const LoadMoreBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 88px;
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  cursor: pointer;
`;

const Tickets = () => {
  const ticketsFeed = useSelector(
    ({ paginator }) => paginator.tickets?.['plus-history'] || {}
  );
  const isEducator = useEducatorContext();
  const userRole = isEducator ? 'educator' : 'learner';
  const jwtToken = useSelector(({ auth }) => auth?.me?.jwtToken || '');
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchTickets({ jwtToken, role: userRole }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadMore = () =>
    dispatch(fetchTickets({ nextPage: true, jwtToken, role: userRole }));

  const openTicketDetails = (ticketId) => {
    window.open(
      `https://support-bot.unacademy.com/history/ticket?ticket_id=${ticketId}&role=learner&token=${jwtToken}`
    );
  };

  const pageSize = 3; // default value is 10 from  back-end

  return ticketsFeed?.results?.length > 0 ? (
    <Wrapper>
      <SubTitle color="var(--color-text-primary)">Previous issues</SubTitle>
      {ticketsFeed?.results?.map((ticket) => {
        const isInProgress = ticket.status?.toLowerCase() === 'in progress';
        return (
          <Ticket key={ticket.id} onClick={() => openTicketDetails(ticket.id)}>
            <Logo>
              <img
                src={getImage('helpCenter/unacademy.svg')}
                alt="unacademy logo"
              />
            </Logo>
            <div>
              <TopContainer>
                <H6 color="var(--color-text-primary)">{ticket.subject}</H6>
                <P2>{`Ticket ID: ${ticket.id}`}</P2>
              </TopContainer>
              <Container>
                <P2 color="var(--color-text-secondary)">
                  {`${dateHelper.getDateMonthString(
                    ticket.created_at,
                    false,
                    false
                  )}, ${dateHelper.formatTime(ticket.created_at)}`}
                </P2>
                <StatusContainer>
                  <StatusLabel color="var(--color-text-primary)">
                    Status:
                  </StatusLabel>
                  <Tag
                    type={isInProgress ? 'hollow' : 'live'}
                    bgColor={
                      isInProgress ? null : 'var(--color-text-secondary)'
                    }
                    $isInProgress={isInProgress}
                  >
                    {ticket.status}
                  </Tag>
                </StatusContainer>
              </Container>
            </div>
          </Ticket>
        );
      })}
      {ticketsFeed?.next && ticketsFeed?.results?.length % pageSize === 0 && (
        <LoadMoreBtn onClick={loadMore}>
          <H6 color="var(--color-text-primary)">+ Show More</H6>
        </LoadMoreBtn>
      )}
    </Wrapper>
  ) : null;
};

export default Tickets;
